"use client";
import React from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import { EventData } from "@/types/common";
import { GA4 } from "@/utils/ga4Utils";

type MarketingLinkProps = {
  path: string;
  children: React.ReactNode;
  className?: string;
  module_location?: string;
  module_name?: string;
  text?: string;
  ariaLabel?: string;
  type?: string;
};

// Utility function to extract plain text from React children

const getPlainTextFromChildren = (children: React.ReactNode): string => {
  let text = "";

  if (typeof children === "string") {
    text = children;
  } else if (Array.isArray(children)) {
    text = children.map(getPlainTextFromChildren).join("");
  } else if (React.isValidElement(children)) {
    const elementProps = children.props as { children?: React.ReactNode; text?: string };
    if (elementProps?.children) {
      text = getPlainTextFromChildren(elementProps.children);
    } else if (elementProps?.text) {
      text = elementProps.text;
    }
  }

  // Clean the accumulated text before returning
  return text
    .replace(/<[^>]*>/g, "") // Remove any tags
    .replace(/[^a-zA-Z0-9\s-_.,]/g, ""); // Remove special characters
};

export default function MarketingLink({
  path,
  children,
  className,
  module_location,
  module_name,
  type = "link",
  ariaLabel = undefined,
}: MarketingLinkProps) {
  const text = getPlainTextFromChildren(children);

  const eventData: EventData = {
    destination: path,
    type,
    text,
  };
  if (module_location) {
    eventData.module_location = module_location;
  }
  if (module_name) {
    eventData.module_name = module_name;
  }

  const handleClick = () => {
    GA4.onMarketingCtaClicked(eventData);
  };

  return (
    <LinkHandler className={className} path={path} onClick={handleClick} ariaLabel={ariaLabel}>
      {children}
    </LinkHandler>
  );
}
